import type { OneOnOnesChatDirectReportsQuery } from 'generated/resources/graphql'

import type { EmployeeWithMeetings } from 'modules/meetings/lib/types/chat'

interface BuildParticipantsResult {
  managedEmployees: EmployeeWithMeetings[]
  peers: EmployeeWithMeetings[]
  mostRecentMeetingId: number
}

// Meeting with content indicators computed from GraphQL data
interface MeetingWithContent {
  id: number
  date: string
  hasNotes: boolean
  hasTranscript: boolean
  otherAttendee: {
    employeeId: string
    name: string
    position: string | null
  } | null
}

export const buildMeetingParticipants = (
  data: OneOnOnesChatDirectReportsQuery | undefined,
  currentEmployeeId: number
): BuildParticipantsResult => {
  if (!data || !currentEmployeeId) {
    return { managedEmployees: [], peers: [], mostRecentMeetingId: 0 }
  }

  const directReportsData = data.directReports?.employeesConnection?.nodes || []
  const allMeetings = data.meetings?.meetingsConnection?.nodes || []

  const managedEmployeeIds = new Set(directReportsData.map((e) => String(e.id)))

  const today = new Date().toISOString().split('T')[0] ?? ''

  const meetingsByEmployee = new Map<string, MeetingWithContent[]>()
  const otherAttendeesByEmployee = new Map<
    string,
    { employeeId: string; name: string; position: string | null }
  >()
  let latestMeetingId = 0
  let latestMeetingDate = ''

  allMeetings.forEach((meeting) => {
    // Only include one-on-one meetings
    if (meeting.meetingType !== 'one_on_one') {
      return
    }

    // Filter out future meetings - only past meetings are candidates for analysis
    if (meeting.date > today) {
      return
    }

    // Find the current user's attendee record to check for notes
    const currentUserAttendee = meeting.attendees?.find(
      (attendee) => attendee.employee?.id === currentEmployeeId
    )

    // Find the other attendee
    const otherAttendee = meeting.attendees?.find(
      (attendee) => attendee.employee?.id !== currentEmployeeId
    )

    if (!otherAttendee?.employee?.id) {
      return
    }

    // Compute content indicators
    // hasNotes: current user's attendee has a page (page_id is set)
    const hasNotes = Boolean(currentUserAttendee?.page?.id)
    // hasTranscript: cannot check from frontend, backend will determine availability
    // We set this to false here - the backend will return actual transcript content if available
    const hasTranscript = false

    const employeeId = String(otherAttendee.employee.id)

    // Build meeting with content indicators
    const meetingWithContent: MeetingWithContent = {
      id: meeting.id,
      date: meeting.date,
      hasNotes,
      hasTranscript,
      otherAttendee: {
        employeeId,
        name: otherAttendee.employee.fullName || 'Unknown',
        position: null,
      },
    }

    // Store meeting
    if (!meetingsByEmployee.has(employeeId)) {
      meetingsByEmployee.set(employeeId, [])
    }
    meetingsByEmployee.get(employeeId)?.push(meetingWithContent)

    // Store other attendee info
    if (!otherAttendeesByEmployee.has(employeeId)) {
      otherAttendeesByEmployee.set(employeeId, {
        employeeId,
        name: otherAttendee.employee.fullName || 'Unknown',
        position: null,
      })
    }

    if (!latestMeetingDate || meeting.date > latestMeetingDate) {
      latestMeetingDate = meeting.date
      latestMeetingId = meeting.id
    }
  })

  // Build managed employees list
  const managedEmployees: EmployeeWithMeetings[] = directReportsData
    .map((employee) => {
      const employeeId = String(employee.id)
      const meetings = meetingsByEmployee.get(employeeId) || []

      return {
        employeeInfo: {
          employeeId,
          name: employee.fullName,
          position: employee.referenceContract?.jobTitle || null,
          managerId: employee.manager?.id ? String(employee.manager.id) : null,
        },
        meetings: meetings,
        goldLayer: null,
      }
    })
    .filter((emp) => emp.meetings.length > 0)

  // Build peers list (meeting attendees who are NOT managed)
  const peers: EmployeeWithMeetings[] = []
  otherAttendeesByEmployee.forEach((attendeeInfo, employeeId) => {
    // Only include if NOT in managed employees
    if (!managedEmployeeIds.has(employeeId)) {
      const meetings = meetingsByEmployee.get(employeeId) || []
      if (meetings.length > 0) {
        peers.push({
          employeeInfo: {
            employeeId,
            name: attendeeInfo.name,
            position: attendeeInfo.position,
            managerId: null,
          },
          meetings: meetings,
          goldLayer: null,
        })
      }
    }
  })

  return {
    managedEmployees,
    peers,
    mostRecentMeetingId: latestMeetingId,
  }
}
